"use client";

import type { OutlineItem } from "@studygenie/documents/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { File, Loader2 } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useState } from "react";
import Dropzone, { type FileRejection } from "react-dropzone";
import { toast } from "sonner";
import * as tus from "tus-js-client";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { TableOfContentsForm } from "@/features/files/components/table-of-contents-form";
import { useCreateStudyset } from "@/features/studysets/api/use-create-studyset";
import { useUserPlanQuery } from "@/features/users/api/user-plan-options";
import { useFreeUploadLimit } from "@/lib/hooks/use-free-upload-limit";
import usePricingModal, { PAYWALL_TYPES } from "@/lib/hooks/use-pricing-modal";
import { cn, sanitizeFileName } from "@/lib/utils";
import { useI18n } from "@/locales/client";
import { useTRPC } from "@/trpc/client";
import { createClient } from "../../../utils/supabase/client";

// TODO: change file size limits
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_FILE_SIZE_FREE_PLAN = 25 * 1024 * 1024; // 25MB

type Props = {
  id?: string;
  onClose?: () => void;
  resetForm?: () => void;
  className?: string;
  onPhaseChange?: (phase: "upload" | "select_sections") => void;
};

export const UploadDropZone = ({
  id,
  onClose,
  resetForm,
  className,
  onPhaseChange,
}: Props) => {
  const trpc = useTRPC();
  const fileMutation = useMutation(
    trpc.questions.generateFromFile.mutationOptions({
      onError: (error) => {
        console.error(error);
        toast.error(error.message);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: trpc.users.getPlanLimit.queryKey(),
        });
      },
    }),
  );

  const [tableOfContents, setTableOfContents] = useState<OutlineItem[] | null>(
    null,
  );
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [uploadedStudySetId, setUploadedStudySetId] = useState<
    string | undefined
  >(id);

  const tableOfContentsMutation = useMutation(
    trpc.files.getTableOfContents.mutationOptions({
      onError: (error) => {
        console.error(error);
        toast.error(error.message);
      },
    }),
  );
  const { data, status } = useUserPlanQuery();
  const queryClient = useQueryClient();
  const [isUpLoading, setIsUpLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const router = useRouter();
  const posthog = usePostHog();
  const { onOpen } = usePricingModal();
  const createStudySetMutation = useCreateStudyset();
  const t = useI18n();
  const freeUploadLimit = useFreeUploadLimit();

  async function uploadFile({
    bucketName,
    storagePath,
    file,
  }: {
    bucketName: string;
    storagePath: string;
    file: File;
  }) {
    const supabase = createClient();
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error("unauthenticated");
    return new Promise((resolve, reject) => {
      const upload = new tus.Upload(file, {
        endpoint:
          process.env.NODE_ENV === "production"
            ? `${process.env
                .NEXT_PUBLIC_SUPABASE_URL!}/storage/v1/upload/resumable`
            : "http://127.0.0.1:54321/storage/v1/upload/resumable",
        retryDelays: [0, 3000, 5000, 10000, 20000],
        headers: {
          authorization: `Bearer ${session?.access_token}`,
          "x-upsert": "true",
        },
        uploadDataDuringCreation: true,
        removeFingerprintOnSuccess: true,
        metadata: {
          bucketName: bucketName,
          objectName: storagePath,
          contentType: file.type,
          cacheControl: "3600",
        },
        chunkSize: 6 * 1024 * 1024, // NOTE: it must be set to 6MB (for now) do not change it
        onError: (error) => {
          console.log(`Failed because: ${error}`);
          if (error instanceof tus.DetailedError) {
            const response = error.originalResponse?.getUnderlyingObject();
            if (
              response &&
              response.responseText ===
                "new row violates row-level security policy"
            ) {
              reject(new Error(t("newStudySetDrawer.errors.fileExists")));
            } else {
              reject(error);
            }
          } else {
            reject(error);
          }
        },
        onProgress: (bytesUploaded, bytesTotal) => {
          const percentage = (bytesUploaded / bytesTotal) * 100;
          setUploadProgress(percentage);
        },
        onSuccess: async (_data) => {
          console.log("Download %s from %s", upload.file, upload.url);
          resolve(upload.url);
        },
      });

      // Check if there are any previous uploads to continue.
      return upload.findPreviousUploads().then((previousUploads) => {
        // Found previous uploads so we select the first one.
        if (previousUploads.length) {
          upload.resumeFromPreviousUpload(previousUploads[0]);
        }

        // Start the upload
        upload.start();
      });
    });
  }

  if (status === "pending") {
    return (
      <Skeleton
        className={cn(
          "border border-dotted border-input rounded-lg",
          className,
        )}
      >
        <div
          className={cn(
            "flex flex-col items-center justify-center w-full h-full rounded-lg bg-gray-50 hover:bg-gray-100",
          )}
        >
          <div className="flex flex-col items-center justify-center pt-5 pb-6 gap-4">
            <div className="flex items-center justify-center gap-4">
              <Image src="/Word.png" height={20.7} width={22.62} alt="word" />
              <Image
                src="/PowerPoint.png"
                height={20.7}
                width={22.62}
                alt="powerpoint"
              />
              <Image
                src="/text_line_pdf.png"
                height={20}
                width={17.5}
                alt="pdf"
              />
            </div>
            <div className="flex items-center justify-center gap-2">
              <Loader2 size={16} className="animate-spin text-blue-500" />
              <p className="text-sm text-[#09090B]">{t("loading")}</p>
            </div>
          </div>
        </div>
      </Skeleton>
    );
  }

  if (status === "error") {
    return (
      <p className="text-sm font-medium text-destructive">
        {t("newStudySetDrawer.errors.generic")}
      </p>
    );
  }
  const hasReachedLimit =
    !data.isSubscribed && data.generationCount >= freeUploadLimit;

  const handleSubmit = async (
    file: File,
    options?: { selectedOutline?: { title: string; page: number }[] | null },
  ) => {
    setIsUpLoading(true);
    try {
      const sanitizedFileName = sanitizeFileName(file.name);
      let studySetId = id;
      if (!studySetId) {
        const data = await createStudySetMutation.mutateAsync({
          name: file.name.slice(0, 50),
        });
        studySetId = data.id;
        setUploadedStudySetId(studySetId);
        posthog.capture("study_set_created", {
          studySetId: data.id,
          userId: data.userId,
          creationMethod: "file_upload",
        });
      }
      const storagePath = `${data.userId}/${studySetId}/${sanitizedFileName}`;
      await uploadFile({
        bucketName: "files",
        storagePath,
        file: file,
      });

      // If PDF and no sections selected yet, fetch TOC using the uploaded file URL
      if (file.type === "application/pdf" && !options?.selectedOutline) {
        const toc = await tableOfContentsMutation.mutateAsync({
          storagePath,
        });
        const normalizedToc = Array.isArray(toc) ? toc : null;
        if (normalizedToc && normalizedToc.length > 1) {
          setTableOfContents(normalizedToc);
          setUploadedFileName(sanitizedFileName);
          onPhaseChange?.("select_sections");
          return;
        }
      }

      // Proceed to processing
      await fileMutation.mutateAsync({
        studySetId: studySetId,
        fileName: sanitizedFileName,
        selectedOutline: options?.selectedOutline ?? undefined,
      });

      if (!id) {
        router.push(`/studysets/${studySetId}/loading`);
      }
      onClose?.();
      resetForm?.();
      toast.success(t("newStudySetDrawer.processingFile"));
      queryClient.invalidateQueries({
        queryKey: trpc.studysets.getById.queryKey({ id: studySetId }),
      });
    } catch (error: unknown) {
      if (error instanceof Error) {
        if (
          error.message ===
          "This file already exists in your study set. To proceed upload a new, unique file."
        ) {
          toast.error(error.message);
        } else {
          toast.error(t("newStudySetDrawer.errors.generic"));
          posthog.captureException(error, {
            name: "file_upload_failed",
            userId: data.userId,
            studySetId: id,
          });
        }
      } else {
        // Handle cases where the error is not an instance of Error
        console.error("An unexpected error occurred", error);
        toast.error(t("newStudySetDrawer.errors.generic"));
        posthog.captureException(error, {
          name: "file_upload_failed",
          userId: data.userId,
          studySetId: id,
        });
      }
      setUploadProgress(0); // Reset or handle progress on error
    } finally {
      setIsUpLoading(false);
      // Do not reset state if we switched to selection phase — finalized later
    }
  };

  const finalizeProcessing = async (
    selectedOutline: { title: string; page: number }[],
  ) => {
    setIsUpLoading(true);
    try {
      if (!uploadedFileName || !uploadedStudySetId) {
        throw new Error("Missing uploaded file context");
      }

      await fileMutation.mutateAsync({
        studySetId: uploadedStudySetId,
        fileName: uploadedFileName,
        selectedOutline,
      });

      if (!id) {
        router.push(`/studysets/${uploadedStudySetId}/loading`);
      }
      onClose?.();
      resetForm?.();
      toast.success(t("newStudySetDrawer.processingFile"));
      queryClient.invalidateQueries({
        queryKey: trpc.studysets.getById.queryKey({ id: uploadedStudySetId }),
      });
    } catch (error: unknown) {
      if (error instanceof Error) {
        toast.error(error.message || t("newStudySetDrawer.errors.uploadFile"));
      } else {
        console.error("An unexpected error occurred", error);
        toast.error(t("newStudySetDrawer.errors.generic"));
      }
    } finally {
      setIsUpLoading(false);
      setTableOfContents(null);
      setUploadedStudySetId(undefined);
      setUploadedFileName(null);
      onPhaseChange?.("upload");
    }
  };

  return (
    <Dropzone
      maxSize={MAX_FILE_SIZE}
      accept={{
        "application/pdf": [".pdf"],
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
          [".docx"],
        "application/vnd.openxmlformats-officedocument.presentationml.presentation":
          [".pptx"],
      }}
      onError={(error) => toast.error(error.message)}
      multiple={false}
      onDropRejected={([reject]: FileRejection[]) => {
        if (reject?.errors.find(({ code }) => code === "file-too-large")) {
          posthog.capture("file_upload_rejected", {
            userId: data.userId,
            fileSize: reject.file.size,
            fileSizeLimit: MAX_FILE_SIZE,
            userPlan: data.isSubscribed ? "premium" : "free",
          });
          toast.error(t("newStudySetDrawer.errors.fileSizeLimit"));
        } else if (
          reject?.errors.find(({ code }) => code === "file-invalid-type")
        ) {
          toast.error(t("newStudySetDrawer.errors.fileTypeNotAllowed"));
        } else {
          toast.error(t("newStudySetDrawer.errors.uploadFile"));
        }
      }}
      onDrop={async (acceptedFile) => {
        if (acceptedFile.length === 0) {
          toast.error(t("newStudySetDrawer.errors.uploadFile"));
          return;
        }
        if (hasReachedLimit) {
          onOpen({
            upgradeReason: t("newStudySetDrawer.upgradeReason"),
            description: t("newStudySetDrawer.upgradeDescription"),
            paywallType: PAYWALL_TYPES.FILE_UPLOAD,
          });
          posthog.capture("paywall_shown", {
            userId: data.userId,
            paywallType: PAYWALL_TYPES.FILE_UPLOAD,
            generationCount: data.generationCount,
          });
          onClose?.();
          return;
        }

        if (
          !data.isSubscribed &&
          acceptedFile[0].size > MAX_FILE_SIZE_FREE_PLAN
        ) {
          toast.error(t("newStudySetDrawer.errors.fileSizeLimitFree"));
          onOpen({
            upgradeReason: t("newStudySetDrawer.upgradeReasonFileSize"),
            description: t("newStudySetDrawer.upgradeDescriptionFileSize"),
            paywallType: PAYWALL_TYPES.FILE_UPLOAD_EXCEEDS_SIZE_LIMIT,
          });
          posthog.capture("paywall_shown", {
            userId: data.userId,
            paywallType: PAYWALL_TYPES.FILE_UPLOAD_EXCEEDS_SIZE_LIMIT,
            generationCount: data.generationCount,
          });
          onClose?.();
          return;
        }

        const file = acceptedFile[0];
        await handleSubmit(file);
      }}
    >
      {({ getRootProps, getInputProps, acceptedFiles }) => (
        <>
          {tableOfContents ? (
            <TableOfContentsForm
              tableOfContents={tableOfContents}
              onCancel={() => {
                setTableOfContents(null);
                onPhaseChange?.("upload");
              }}
              onSubmit={finalizeProcessing}
              location={!id ? "newStudySetDrawer" : "uploadFileModal"}
            />
          ) : (
            <div
              onClick={(e) => e.stopPropagation()}
              {...getRootProps()}
              className={cn(
                "border border-dotted border-input rounded-lg",
                className,
              )}
            >
              <div
                className={cn(
                  "flex flex-col items-center justify-center w-full h-full rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100",
                )}
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6 gap-4">
                  <div className="flex items-center justify-center gap-4">
                    <Image
                      src="/Word.png"
                      height={20.7}
                      width={22.62}
                      alt="word"
                    />
                    <Image
                      src="/PowerPoint.png"
                      height={20.7}
                      width={22.62}
                      alt="powerpoint"
                    />
                    <Image
                      src="/text_line_pdf.png"
                      height={20}
                      width={17.5}
                      alt="pdf"
                    />
                  </div>
                  <p className="mb-2 text-sm text-foreground font-light">
                    <span className="font-medium">
                      {t("newStudySetDrawer.clickToUpload")}
                    </span>{" "}
                    {t("newStudySetDrawer.dragAndDrop")}
                  </p>
                </div>
                {acceptedFiles?.[0] ? (
                  <div className="max-w-xs bg-white flex items-center rounded-md overflow-hidden outline outline-[1px] outline-zinc-200 divide-x divide-zinc-200 mx-2">
                    <div className="px-3 py-2 h-full grid place-items-center">
                      <File className="h-4 w-4 text-blue-500" />
                    </div>
                    <div className="px-3 py-2 h-full text-sm truncate">
                      {acceptedFiles[0].name}
                    </div>
                  </div>
                ) : null}
                {isUpLoading ? (
                  <div className="w-full mt-4 max-w-xs mx-auto">
                    <Progress
                      className="h-1 w-full bg-zinc-200"
                      value={uploadProgress}
                    />
                  </div>
                ) : null}
                <input
                  multiple={false}
                  accept="application/pdf, application/vnd.openxmlformats-officedocument.presentationml.presentation,
                    application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/msword"
                  type="file "
                  {...getInputProps()}
                  id="dropzone-file"
                  className="hidden"
                />
              </div>
            </div>
          )}
        </>
      )}
    </Dropzone>
  );
};
